import { useCallback } from 'react';

import orderBy from 'lodash.orderby';
import type { UseFormReturn } from 'react-hook-form';

import { noop, queryClient, snackbar } from '@/_shared';

import { useDeliverableRevisionHistory } from './use-deliverable-revision-history';
import { useEditableState } from './use-editable-state';

import type { DeliverablesFormSchema, DeliverableWholesalePriceUpdateSchema } from '../deliverables';
import { useCreateMutation, useDeliverableDeleteMutation } from '../deliverables/api';
import { deliverableFormToQuery, deliverableQueryToForm } from '../deliverables/helper';
import { useCreateWholeSaleDiscountPriceMutation } from '../deliverables/wholesale-api';
import { wholesalePriceFormToUpdate } from '../deliverables/wholesale-prices';
import { REVENUE_LOCKDOWN_NO_OVERRIDE_PERMISSION_MESSAGE } from '../revenue-lockdown-guard';
import type { AgreementFormSchema } from '../schemas';

type LockdownOverrideArgs = { pin: string; justification: string };

type DeliverablesExecutionLockOpts = {
  isExecutionDateLocked: boolean;
  canOverrideLockdown: boolean;
  requestRevenueLockdownOverride: (opts: {
    affectedFields: string[];
    onConfirm: (args: LockdownOverrideArgs) => void | Promise<void>;
  }) => void;
};

export function useDeliverablePage(
  form: UseFormReturn<AgreementFormSchema>,
  isEditable: boolean,
  executionLock?: DeliverablesExecutionLockOpts
) {
  const fromAgreementDeliverables = form.watch('agreement_deliverables');
  const { mutateAsync: deleteAsync } = useDeliverableDeleteMutation();
  const { mutateAsync } = useCreateMutation();
  const { mutateAsync: createWholeSaleDiscountPrice } = useCreateWholeSaleDiscountPriceMutation();

  const deliverableEditState = useEditableState(form, fromAgreementDeliverables, isEditable);
  const { localDeliverableData, resetFormState } = deliverableEditState;

  const deleteDeliverable = useCallback(
    async (oId: number, override?: LockdownOverrideArgs) => {
      const res = await deleteAsync({
        id: oId,
        lockdown_override_pin: override?.pin,
        override_justification_note: override?.justification,
      })
        .then(() => {
          const newValue = form.getValues('agreement_deliverables').filter((item) => item.id !== oId);
          form.setValue('agreement_deliverables', newValue, { shouldDirty: true });
          resetFormState();
          snackbar.success('Deliverable has been deleted.');
          queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY'] });
        })
        .catch(noop);
      return res;
    },
    [deleteAsync, form, resetFormState]
  );

  const handleDeliverableDelete = useCallback(
    async (oId: number) => {
      if (executionLock?.isExecutionDateLocked) {
        if (!executionLock.canOverrideLockdown) {
          snackbar.error(REVENUE_LOCKDOWN_NO_OVERRIDE_PERMISSION_MESSAGE);
          return;
        }

        executionLock.requestRevenueLockdownOverride({
          affectedFields: ['Agreement Execution Date', 'Deliverables'],
          onConfirm: (args) => deleteDeliverable(oId, args),
        });
        return;
      }

      await deleteDeliverable(oId);
    },
    [deleteDeliverable, executionLock]
  );

  const onCreate = useCallback(
    (d: DeliverablesFormSchema) => {
      const oldV = form.getValues();
      form.reset({ ...oldV, agreement_deliverables: [...oldV.agreement_deliverables, d] }, { keepDirty: true });
    },
    [form]
  );

  const handleDeliverableCreate = useCallback(
    async (dto: DeliverablesFormSchema, lockdownOverridePin?: string, overrideJustificationNote?: string) => {
      const sortedData = orderBy(localDeliverableData, ['sort_order'], ['desc']);
      const localNextSortOrder = (sortedData.length > 0 ? sortedData[0].sort_order : 0) + 1;

      const response = await mutateAsync({
        ...deliverableFormToQuery([{ ...dto, sort_order: localNextSortOrder }])[0],
        lockdown_override_pin: lockdownOverridePin,
        override_justification_note: overrideJustificationNote,
      });
      if (response) {
        const { data } = response;

        let wsPayload = [] as DeliverableWholesalePriceUpdateSchema[];
        let wsPayloadResponse = [] as DeliverableWholesalePriceUpdateSchema[];

        if (data && dto.product) {
          if (Array.isArray(dto.deliverable_wholesale_price) && dto.deliverable_wholesale_price.length > 0) {
            wsPayload = wholesalePriceFormToUpdate(dto.deliverable_wholesale_price, data.id);
            wsPayloadResponse = await createWholeSaleDiscountPrice({
              items: wsPayload,
              lockdown_override_pin: lockdownOverridePin,
              override_justification_note: overrideJustificationNote,
            });
          }
          snackbar.success('New Deliverable has been added.');

          onCreate(
            deliverableQueryToForm([
              {
                ...data,
                price_frequency: dto.price_frequency,
                product: dto.product,
                sku: dto.product.sku,
                deliverable_wholesale_price: wsPayloadResponse,
              },
            ])[0]
          );
          queryClient.removeQueries({ queryKey: ['AGREEMENTS'] });
          queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY'] });
        }
      }

      return response;
    },
    [createWholeSaleDiscountPrice, localDeliverableData, mutateAsync, onCreate]
  );

  const handleDeliverableUpdate = useCallback(
    async (data: DeliverablesFormSchema) => {
      const oldV = form.getValues();
      const dv = oldV.agreement_deliverables;

      const nD = dv.map((row) => {
        if (row.id === data.id) {
          return {
            ...row,
            ...data,
            matchingDateRange: null,
          };
        }
        return { ...row, matchingDateRange: null };
      });
      form.reset({ ...oldV, agreement_deliverables: [...nD] }, { keepDirty: true });

      queryClient.removeQueries({ queryKey: ['AGREEMENTS'] });
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY'] });
      return true;
    },
    [form]
  );

  const deliverableRevisionHistory = useDeliverableRevisionHistory(
    form,
    handleDeliverableDelete,
    handleDeliverableCreate
  );
  return {
    fromAgreementDeliverables,
    deliverableRevisionHistory,
    deliverableEditState,
    handleDeliverableDelete,
    handleDeliverableCreate,
    handleDeliverableUpdate,
  };
}

export type UseDeliverablePage = ReturnType<typeof useDeliverablePage>;
